import React, { useState, useEffect } from 'react';
import { superAdminApi } from '../../services/superAdminApi';
import { getAvailableThemes } from '../themes';

const AVAILABLE_THEMES = getAvailableThemes();

const TenantsTable = ({ status, onTenantUpdated }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    fetchTenants();
  }, [status]);

  const fetchTenants = async (page = 1) => {
    try {
      setLoading(true);
      const params = { status, page, per_page: 15 };
      const response = await superAdminApi.getTenants(params);
      
      // Handle both response structures
      if (response.data.data) {
        setTenants(response.data.data.tenants || []);
        setPagination(response.data.data.pagination || null);
      } else {
        setTenants(response.data.tenants || []);
        setPagination(response.data.pagination || null);
      }
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      setTenants([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (tenantId) => {
    try {
      await superAdminApi.activateTenant(tenantId);
      fetchTenants();
      onTenantUpdated && onTenantUpdated();
    } catch (error) {
      console.error('Failed to activate tenant:', error);
    }
  };

  const handleSuspend = async (tenantId) => {
    const reason = prompt('Enter suspension reason:');
    if (!reason) return;

    try {
      await superAdminApi.suspendTenant(tenantId, { reason });
      fetchTenants();
      onTenantUpdated && onTenantUpdated();
    } catch (error) {
      console.error('Failed to suspend tenant:', error);
    }
  };

  const handleThemeChange = async (tenantId, newTheme) => {
    try {
      await superAdminApi.updateTenant(tenantId, { theme: newTheme });
      fetchTenants();
      onTenantUpdated && onTenantUpdated();
    } catch (error) {
      console.error('Failed to update tenant theme:', error);
      alert('Failed to update theme. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { class: 'status-active', icon: 'check-circle', text: 'Active' },
      pending: { class: 'status-pending', icon: 'clock', text: 'Pending' },
      suspended: { class: 'status-suspended', icon: 'pause-circle', text: 'Suspended' },
      cancelled: { class: 'status-cancelled', icon: 'times-circle', text: 'Cancelled' }
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`status-badge ${badge.class}`}>
        <i className={`fas fa-${badge.icon}`}></i>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="tenants-table">
      <div className="table-container">
        {loading ? (
          <div className="table-loading">
            <i className="fas fa-spinner fa-spin"></i>
            Loading tenants...
          </div>
        ) : (
          <table className="tenants-table-element">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Contact</th>
                <th>Domain</th>
                <th>Theme</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    No tenants found
                  </td>
                </tr>
              ) : (
                tenants.map(tenant => (
                  <tr key={tenant.id}>
                    <td>
                      <strong>{tenant.name}</strong>
                      <div className="tenant-slug">/{tenant.slug}</div>
                    </td>
                    <td>
                      <div>{tenant.contact_email}</div>
                      {tenant.contact_phone && (
                        <div className="contact-phone">{tenant.contact_phone}</div>
                      )}
                    </td>
                    <td>
                      {tenant.domain || (tenant.subdomain ? `${tenant.subdomain}.abfisaas.com` : 'Not set')}
                    </td>
                    <td>
                      <select
                        value={tenant.theme || 'default'}
                        onChange={(e) => handleThemeChange(tenant.id, e.target.value)}
                        className="theme-select"
                      >
                        {AVAILABLE_THEMES.map(theme => (
                          <option key={theme.id} value={theme.id}>
                            {theme.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{getStatusBadge(tenant.status)}</td>
                    <td>
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </td>
                    <td className="actions-cell">
                      {tenant.status === 'suspended' && (
                        <button
                          onClick={() => handleActivate(tenant.id)}
                          className="btn-success btn-sm"
                          title="Activate"
                        >
                          <i className="fas fa-play"></i>
                        </button>
                      )}
                      {tenant.status === 'active' && (
                        <button
                          onClick={() => handleSuspend(tenant.id)}
                          className="btn-warning btn-sm"
                          title="Suspend"
                        >
                          <i className="fas fa-pause"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TenantsTable;